import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Typography from '../../../atoms/Typography'
import { Input } from '../../../atoms/inputs/Input'
import { TextArea } from '../../../atoms/inputs/InputTextArea'
import { IconFileUpload, IconInfoCircle, IconWorld } from '@tabler/icons-react'
import { cn } from 'src/enviroment/lib/utils'
import { SecondaryButton } from '../../../atoms/buttons/SecondaryButton'
import { PrimaryButton } from '../../../atoms/buttons/PrimaryButton'
import { api } from '../../../../api/root'
import { useNavigate } from 'react-router'
import { ROUTES } from 'src/types/routes'
import { useChatStore } from '../../../../enviroment/store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { triggerCopaProactiveResponse } from '../../../../enviroment/api/chat'
import { useTenantStore } from '../../../../enviroment/store/tenentStore'
import useAuth from '../../../../enviroment/hooks/useAuth'
import { ErrorMessage } from 'src/components/atoms/ErrorMessage'
import SingleFileDropzoneForCopa from '../../copa-dropzone/SingleFileDropzoneForCopa'
import MultiFileDropzoneForCopa from '../../copa-dropzone/MultiFileDropzoneForCopa'
import TooltipWrapper from 'src/components/atoms/TooltipWrapper'
import { formatFileSize } from 'src/enviroment/lib/formatters/format-file-size'
import { ACCEPT, SUPPORTED_FORMATS_LABEL } from 'src/components/organisms/data-room/DataRoomFileDropzone'
import { isValidFile } from 'src/enviroment/utils/isValidFile'
import { MAX_FILE_SIZE_BYTES } from 'src/enviroment/constants/constants'
import { projectName } from 'src/enviroment/validation/fields'
import { createGeoJSONFileFromFeatures } from '../../../../enviroment/utils/createGeoJSONFileFromFeatures'

const MAX_DATA_ROOM_FILES = 10

interface CreateProjectMessageProps {
  message: string
  onMinimize?: () => void
}

const projectFormSchema = z.object({
  name: projectName,
  userContext: z
    .string()
    .trim()
    .max(1000, { message: 'Context must be 1000 characters or less' })
    .refine((value) => value.length === 0 || value.length >= 20, {
      message: 'Please use at least 20 characters, or leave it empty',
    }),
  shape: z.instanceof(File).nullable(),
})

type ProjectFormData = z.infer<typeof projectFormSchema>

const CreateProjectMessage: React.FC<CreateProjectMessageProps> = ({ message, onMinimize }) => {
  const navigate = useNavigate()
  const { mutateAsync: createProject, isPending } = api.project.createWithDocuments.useMutation()
  const {
    setIsExpanded,
    upsertMessageByType,
    createProjectFormData,
    setCreateProjectFormData,
    sessionId,
    setSessionId,
    removeMessageByType,
    updateLastAssistantMessage,
    updateLastAssistantThinking,
    setLastAssistantThinkingDone,
    removeLastAssistantMessage,
    addMessage,
    startGeneration,
    stopGeneration,
  } = useChatStore(
    useShallow((s) => ({
      setIsExpanded: s.setIsExpanded,
      upsertMessageByType: s.upsertMessageByType,
      createProjectFormData: s.createProjectFormData,
      setCreateProjectFormData: s.setCreateProjectFormData,
      sessionId: s.sessionId,
      setSessionId: s.setSessionId,
      removeMessageByType: s.removeMessageByType,
      updateLastAssistantMessage: s.updateLastAssistantMessage,
      updateLastAssistantThinking: s.updateLastAssistantThinking,
      setLastAssistantThinkingDone: s.setLastAssistantThinkingDone,
      removeLastAssistantMessage: s.removeLastAssistantMessage,
      addMessage: s.addMessage,
      startGeneration: s.startGeneration,
      stopGeneration: s.stopGeneration,
    })),
  )
  const user = useAuth()
  const currentTenant = useTenantStore((s) => s.currentTenant)
  const userId = user?.user?.id ?? ''
  const tenantId = currentTenant?.id ?? ''
  const [showUploadArea, setShowUploadArea] = useState(false)
  const [dataRoomFiles, setDataRoomFiles] = useState<File[]>([])
  const [showDataRoomDropzone, setShowDataRoomDropzone] = useState(false)
  const isDataRoomFull = dataRoomFiles.length >= MAX_DATA_ROOM_FILES
  // Generated up front so documents can be uploaded to the data room before the project row exists.
  const projectIdRef = useRef(crypto.randomUUID())

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
  } = useForm<ProjectFormData>({
    mode: 'onChange',
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: createProjectFormData?.name || '',
      userContext: createProjectFormData?.userContext || '',
      shape: createGeoJSONFileFromFeatures(createProjectFormData?.drawnFeatures),
    },
  })

  const onSubmit = async (data: ProjectFormData) => {
    if (!data.shape) {
      console.error('File is required')
      return
    }

    const projectId = projectIdRef.current

    try {
      await createProject({
        id: projectId,
        name: data.name.trim(),
        ...(data.userContext && { userContext: data.userContext }),
        shape: data.shape,
        documents: dataRoomFiles,
      })

      setCreateProjectFormData(null)
      removeMessageByType('create-project')
      navigate(ROUTES.PROJECTS, { state: { keepWidgetOpen: true } })

      if (userId && tenantId) {
        const abortController = new AbortController()
        startGeneration(abortController)
        try {
          await triggerCopaProactiveResponse(
            { userId, tenantId, sessionId },
            {
              onSessionCreated: (id) => setSessionId(id),
              onMessageAdded: (id) => {
                setIsExpanded(true)
                addMessage({ id, role: 'assistant', content: '', date: new Date().toISOString(), isLoading: true })
              },
              onChunk: (chunk) => updateLastAssistantMessage(chunk),
              onFirstChunk: () => setIsExpanded(true),
              onThinking: (step) => updateLastAssistantThinking(step),
              onThinkingDone: (durationMessage) => setLastAssistantThinkingDone(durationMessage),
              onError: (id) => removeLastAssistantMessage(id),
            },
            abortController.signal,
          )
        } finally {
          stopGeneration()
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('exceeds maximum allowed for')) {
        upsertMessageByType('area-limit')
        setIsExpanded(true)
      }
      console.error('Failed to create project:', error)
    }
  }

  const handleSelectInAtlas = () => {
    setCreateProjectFormData({
      name: watch('name'),
      userContext: watch('userContext'),
      drawnFeatures: createProjectFormData?.drawnFeatures,
    })

    setValue('shape', null)

    const navigateState = { keepWidgetOpen: true, drawToolsEnabled: true }
    navigate(ROUTES.ATLAS, { state: navigateState })
  }

  const handleUploadFile = () => {
    setShowUploadArea(true)
    setCreateProjectFormData({ drawnFeatures: null })
  }

  const handleFileChange = (file: File | null) => {
    setValue('shape', file)
    setShowUploadArea(false)
    setCreateProjectFormData({ drawnFeatures: null })
  }

  useEffect(() => {
    if (createProjectFormData?.drawnFeatures && createProjectFormData.drawnFeatures.length > 0) {
      setValue('shape', createGeoJSONFileFromFeatures(createProjectFormData.drawnFeatures))
    } else {
      setValue('shape', null)
    }
  }, [createProjectFormData?.drawnFeatures, setValue])

  return (
    <div className="flex w-full flex-col gap-[10px] p-3">
      <Typography variant="sm-medium" className="whitespace-pre-wrap leading-[20px] text-light-green-50">
        {message}
      </Typography>

      <div className="v rounded-lg border border-gray-700 bg-gray-900 p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-5">
          {/* Project Details Section */}
          <div className="flex w-full flex-col gap-4">
            <Typography variant="sm-bold" className="leading-[20px] text-gray-50">
              Project Details
            </Typography>

            <div className="flex w-full flex-col gap-6">
              {/* Project Name */}
              <div className="w-full">
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <>
                      <Input
                        {...field}
                        label="Project Name*"
                        placeholder="e.g., Amazon Rainforest Restoration"
                        value={value}
                        onChange={onChange}
                        autoFocus
                        className="h-[44px] border-gray-300 bg-gray-800 text-sm text-gray-50 placeholder:text-gray-200"
                      />
                      <ErrorMessage message={errors.name?.message} />
                    </>
                  )}
                />
              </div>

              {/* Project Context */}
              <div className="w-full">
                <Controller
                  name="userContext"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <>
                      <TextArea
                        {...field}
                        label="Tell me about your project (optional)"
                        placeholder="Describe the project for me — what it does, who it's for, the problem it solves, what stage it's at, and what makes it stand out. I'll use this to curate the most relevant data for it."
                        value={value ?? ''}
                        onChange={onChange}
                        rows={4}
                        maxLength={1000}
                        className="border-gray-300 bg-gray-800 text-sm text-gray-50 placeholder:text-gray-200"
                      />
                      <ErrorMessage message={errors.userContext?.message} />
                    </>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Project Boundary Section */}
          <div className="flex w-full flex-col gap-4">
            <Typography variant="sm-bold" className="leading-[20px] text-gray-50">
              Project Boundary
            </Typography>

            {!showUploadArea && !watch('shape') && (
              <div className="flex gap-2">
                <SecondaryButton
                  type="button"
                  onClick={handleSelectInAtlas}
                  className="h-[28px] self-start rounded-full border-light-green-500 px-4 py-1 text-light-green-50 hover:bg-light-green-500/10 [&>div>div]:!text-[14px] [&>div>div]:!font-normal"
                  rightIcon={<IconWorld className="h-5 w-5 text-light-green-500" stroke={1.5} />}
                >
                  Draw in Atlas
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={handleUploadFile}
                  className="h-[28px] self-start rounded-full border-light-green-500 px-4 py-1 text-light-green-50 hover:bg-light-green-500/10 [&>div>div]:!text-[14px] [&>div>div]:!font-normal"
                  rightIcon={<IconFileUpload className="h-5 w-5 text-light-green-500" stroke={1.5} />}
                >
                  Upload Shapefile
                </SecondaryButton>
              </div>
            )}

            <Controller
              name="shape"
              control={control}
              render={({ field: { value }, formState }) => (
                <>
                  <SingleFileDropzoneForCopa
                    file={value}
                    onFileChange={handleFileChange}
                    showDropzone={showUploadArea}
                    accept={{ 'application/octet-stream': ['.geojson', '.zip', '.kmz', '.kml'] }}
                    title="Drop files here or click to upload"
                    description="Upload a GeoJSON, KML, KMZ, or zip (shapefile or GeoPackage) to begin analyzing"
                    hint={
                      'If you have a spatial file for your project boundary, upload it.\nIf not, simply draw your boundary directly on the map to get started.'
                    }
                    rejectionHint="Please upload GeoJSON, KML, KMZ, or a zip archive. Shapefiles must be zipped with their .shx, .dbf and .prj files; a GeoPackage can be zipped on its own."
                  />
                  <ErrorMessage message={formState.errors.shape?.message} />
                </>
              )}
            />
          </div>

          <div className="flex w-full flex-col gap-4">
            <div className="flex items-center gap-1.5">
              <Typography variant="sm-bold" className="leading-[20px] text-gray-50">
                Project Data Room
              </Typography>
              <TooltipWrapper
                side="top"
                contentClassName="max-w-[200px] px-3 py-2 text-xs leading-[16px]"
                content="Reports, studies, permits — I'll read them while your project is set up."
              >
                <button
                  type="button"
                  aria-label="What can I upload?"
                  className="shrink-0 text-gray-400 hover:text-light-green-500"
                >
                  <IconInfoCircle className="h-4 w-4" stroke={1.5} />
                </button>
              </TooltipWrapper>
            </div>

            {!showDataRoomDropzone && !isDataRoomFull && (
              <SecondaryButton
                type="button"
                onClick={() => setShowDataRoomDropzone(true)}
                className="h-[28px] self-start rounded-full border-light-green-500 px-4 py-1 text-light-green-50 hover:bg-light-green-500/10 [&>div>div]:!text-[14px] [&>div>div]:!font-normal"
                rightIcon={<IconFileUpload className="h-5 w-5 text-light-green-500" stroke={1.5} />}
              >
                {dataRoomFiles.length > 0 ? 'Upload more documents' : 'Upload documents'}
              </SecondaryButton>
            )}

            <MultiFileDropzoneForCopa
              files={dataRoomFiles}
              onFilesChange={setDataRoomFiles}
              showDropzone={showDataRoomDropzone}
              onShowDropzoneChange={setShowDataRoomDropzone}
              accept={{ 'application/octet-stream': ACCEPT.split(',') }}
              acceptAttr={ACCEPT}
              isValidFile={isValidFile}
              maxFiles={MAX_DATA_ROOM_FILES}
              maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
              title="Drop files here or click to upload"
              description={`Upload a ${SUPPORTED_FORMATS_LABEL} file to add it to your data room`}
              hint="Reports, studies, permits — anything about your project."
              rejectionHint={`Unsupported file type. Supported formats: ${SUPPORTED_FORMATS_LABEL}.`}
              maxFilesHint={`You can attach up to ${MAX_DATA_ROOM_FILES} documents. Extra files were not added.`}
              oversizedHint={(oversized) =>
                `${oversized.map((f) => f.name).join(', ')} exceeds the ${formatFileSize(MAX_FILE_SIZE_BYTES)} limit.`
              }
            />
          </div>

          <div className="flex w-full justify-end">
            {isPending && (
              <div className="mb-4 w-full text-center">
                <div className="animate-pulse text-gray-600">
                  <Typography variant="sm">
                    Please wait while we create your project. This process may take a few moments...
                  </Typography>
                </div>
              </div>
            )}
            <PrimaryButton
              type="submit"
              disabled={!isValid || isPending}
              isLoading={isPending}
              className={cn(
                !isValid && 'bg-gray-800 text-gray-700',
                isValid && 'bg-light-green-500 text-gray-50 hover:bg-light-green-600',
                'disabled:cursor-not-allowed disabled:bg-gray-800 disabled:text-gray-700',
                'h-[40px] px-4',
              )}
            >
              Confirm choices
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateProjectMessage
